import React from 'react';
import moment from 'moment';
import { useTranslation } from 'common/i18n/hooks';
import { InputLabelReact } from 'common/components/input/label';
import { InputReact } from 'common/components/input';
import { useExperimentsStore } from 'apps/experiments/src/store';
import { ExperimentCollectionType } from 'apps/experiments/src/types';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { ModalReact } from 'common/components/modal';
import { InputSize, InputType } from 'common/components/input/types';
import { MAX_CONFIDENCE_LEVEL, MAX_SAMPLE_SIZE, MIN_CONFIDENCE_LEVEL, MIN_SAMPLE_SIZE } from 'apps/experiments/src/components/utils';
import { DateRangePickerReact } from 'common/widgets/date-range-picker-new';
import { SelectReact } from 'common/components/select';
import { DateRangeType, DisplayContext } from 'common/widgets/date-range-picker/models/types';
import { SelectTheme } from 'common/components/select/constants';
import { ExperimentDateRange } from 'common/widgets/date-range-picker/models/experiment-date-range';
import { useDraftExperiment } from 'iron/apps/experiments/src/components/hooks/draft-experiment';
import styles from './index.module.styl';
import { CollectionMethodComponents } from './collection-method';
const DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss";
export function ExperimentPageSettingsModal() {
  var _draftExperiment$sett4, _draftExperiment$name, _draftExperiment$hypo, _draftExperiment$sett5, _draftExperiment$sett6;
  const [isDateOpen, setIsDateOpen] = React.useState(false);
  const i18n = useTranslation();
  const experimentState = useExperimentsStore(state => state.experimentState);
  const draftExperiment = useExperimentsStore(state => state.draftExperiment);
  const {
    discardDraft,
    saveDraft,
    updateDraft
  } = useDraftExperiment();

  // The change will trigger a status update if the present time changes which state it is in from (during, before, or after) the date range.
  const dateRangeChangeChanged = () => {
    const startChange = (draftExperiment === null || draftExperiment === void 0 ? void 0 : draftExperiment.start_date) !== (experimentState === null || experimentState === void 0 ? void 0 : experimentState.start_date);
    const endChange = (draftExperiment === null || draftExperiment === void 0 ? void 0 : draftExperiment.end_date) !== (experimentState === null || experimentState === void 0 ? void 0 : experimentState.end_date);
    return startChange || endChange;
  };
  const handleSaveExperiment = React.useCallback(() => {
    saveDraft(!dateRangeChangeChanged());
  }, [saveDraft, dateRangeChangeChanged]);

  // Commenting out data collection method until FF integration is ready
  // const handleCollectionMethod = React.useCallback(
  //   (collectionMethod: ExperimentCollectionType) => {
  //     editExperiment({
  //       settings: {
  //         collectionMethod,
  //       },
  //     });
  //   },
  //   [editedExperiment, editExperiment],
  // );

  const experimentDateRange = React.useMemo(() => {
    if (draftExperiment !== null && draftExperiment !== void 0 && draftExperiment.end_date) {
      return new ExperimentDateRange({
        type: DateRangeType.Between,
        from: draftExperiment === null || draftExperiment === void 0 ? void 0 : draftExperiment.start_date,
        to: draftExperiment === null || draftExperiment === void 0 ? void 0 : draftExperiment.end_date
      });
    }
    if (draftExperiment !== null && draftExperiment !== void 0 && draftExperiment.start_date) {
      return new ExperimentDateRange({
        type: DateRangeType.Since,
        from: draftExperiment === null || draftExperiment === void 0 ? void 0 : draftExperiment.start_date
      });
    }
    return new ExperimentDateRange({
      type: DateRangeType.Between
    });
  }, [draftExperiment === null || draftExperiment === void 0 ? void 0 : draftExperiment.start_date, draftExperiment === null || draftExperiment === void 0 ? void 0 : draftExperiment.end_date]);
  const canSave = React.useMemo(() => {
    var _draftExperiment$sett, _draftExperiment$sett2;
    if (!draftExperiment) return false;
    if (!draftExperiment.name) return false;
    if (!((_draftExperiment$sett = draftExperiment.settings) !== null && _draftExperiment$sett !== void 0 && _draftExperiment$sett.collectionMethod)) return false;
    if (!((_draftExperiment$sett2 = draftExperiment.settings) !== null && _draftExperiment$sett2 !== void 0 && _draftExperiment$sett2.controlKey)) return false;
    if (draftExperiment.settings.collectionMethod === ExperimentCollectionType.EXPOSURE_EVENTS) {
      var _draftExperiment$sett3;
      return !!((_draftExperiment$sett3 = draftExperiment.settings.exposureEventSettings) !== null && _draftExperiment$sett3 !== void 0 && _draftExperiment$sett3.experimentName);
    }
    return true;
  }, [draftExperiment]);
  if (!draftExperiment) {
    return null;
  }

  // Commenting out data collection method until FF integration is ready
  // const COLLECTION_METHODS = [
  //   {
  //     label: i18n.t(`experiments.setup.form.data-collection-options.exposure-events`),
  //     value: ExperimentCollectionType.EXPOSURE_EVENTS,
  //   },
  // ];

  const CollectionMethodComponent = CollectionMethodComponents[(draftExperiment === null || draftExperiment === void 0 || (_draftExperiment$sett4 = draftExperiment.settings) === null || _draftExperiment$sett4 === void 0 ? void 0 : _draftExperiment$sett4.collectionMethod) as keyof typeof CollectionMethodComponents];
  return <ModalReact growToContent open modalTitle={i18n.t("experiments.setup.title")} data-sentry-element="ModalReact" data-sentry-component="ExperimentPageSettingsModal" data-sentry-source-file="index.tsx">
      <div className={styles.container}>
        <h2 className={styles.title}>{i18n.t("experiments.setup.title")}</h2>
        <div className={styles.form}>
          <div>
            <InputLabelReact fieldId='experiment-name' label={i18n.t("experiments.setup.form.name")} necessary='required' data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            <InputReact className={styles.fullWidth} fieldId='experiment-name' value={(_draftExperiment$name = draftExperiment.name) !== null && _draftExperiment$name !== void 0 ? _draftExperiment$name : ""} onInput={ev => {
            updateDraft({
              name: ev.target.value
            });
          }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
          </div>

          <div>
            <InputLabelReact fieldId='experiment-hypothesis' label={i18n.t("experiments.setup.form.hypothesis")} necessary='optional' data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            <InputReact className={styles.fullWidth} fieldId='experiment-hypothesis' size={InputSize.Large} placeholder={i18n.t("experiments.setup.form.hypothesis-placeholder")} value={(_draftExperiment$hypo = draftExperiment.hypothesis) !== null && _draftExperiment$hypo !== void 0 ? _draftExperiment$hypo : ""} type='textarea' onInput={ev => {
            updateDraft({
              hypothesis: ev.target.value
            });
          }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
          </div>

          {/* Commenting out data collection method until FF integration is ready */
          /* <div className={styles.radioGroup}>
            <InputLabelReact
              fieldId='experiment-data-collection'
              label={i18n.t(`experiments.setup.form.data-collection`)}
              necessary='required'
            />
            {COLLECTION_METHODS.map((collectionMethod) => (
              <ListItemReact
              key={collectionMethod.value}
              onClick={() => handleCollectionMethod(collectionMethod.value)}
              >
              <RadioButtonReact
                selected={editedExperiment.settings?.collectionMethod === collectionMethod.value}
                onClick={() => handleCollectionMethod(collectionMethod.value)}
              >
                {collectionMethod.label}
              </RadioButtonReact>
              </ListItemReact>
            ))}
            </div> */}

          <div className={styles.subheading}>{i18n.t("experiments.setup.form.subsection.data-collection")}</div>

          {CollectionMethodComponent && <CollectionMethodComponent />}

          <div className={styles.subheading}>{i18n.t("experiments.setup.form.subsection.analysis")}</div>

          <div>
            <InputLabelReact fieldId='experiment-sample-size' label={i18n.t("experiments.setup.form.sample-size")} necessary='optional' data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            <InputReact className={styles.fullWidth} fieldId='experiment-sample-size' value={String(draftExperiment === null || draftExperiment === void 0 || (_draftExperiment$sett5 = draftExperiment.settings) === null || _draftExperiment$sett5 === void 0 ? void 0 : _draftExperiment$sett5.sampleSize)} min={String(MIN_SAMPLE_SIZE)} max={String(MAX_SAMPLE_SIZE)} type={InputType.Number} onInput={ev => {
            updateDraft({
              settings: {
                sampleSize: Number(ev.target.value)
              }
            });
          }} onChange={(ev: TODO) => {
            updateDraft({
              settings: {
                sampleSize: Number(ev.target.value)
              }
            });
          }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
          </div>

          <div>
            <InputLabelReact fieldId='experiment-confidence-level' label={i18n.t("experiments.setup.form.confidence-level")} necessary='optional' data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            <InputReact className={styles.fullWidth} fieldId='experiment-confidence-level' value={String(draftExperiment === null || draftExperiment === void 0 || (_draftExperiment$sett6 = draftExperiment.settings) === null || _draftExperiment$sett6 === void 0 ? void 0 : _draftExperiment$sett6.confidenceLevel)} type={InputType.Number} min={String(MIN_CONFIDENCE_LEVEL)} max={String(MAX_CONFIDENCE_LEVEL)} step='0.01' onInput={ev => {
            updateDraft({
              settings: {
                confidenceLevel: Number(ev.target.value)
              }
            });
          }} onChange={(ev: TODO) => {
            updateDraft({
              settings: {
                confidenceLevel: Number(ev.target.value)
              }
            });
          }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
          </div>

          <div>
            <InputLabelReact fieldId='experiment-date' label={i18n.t("experiments.setup.form.date")} helpText={i18n.t("experiments.setup.form.date-info")} necessary='optional' data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            <SelectReact className={styles.fullWidth} theme={SelectTheme.Secondary} alwaysShowCaret label={experimentDateRange.getLabel()} onMenuChange={() => setIsDateOpen(!isDateOpen)} open={isDateOpen} data-sentry-element="SelectReact" data-sentry-source-file="index.tsx">
              <div slot='body'>
                <DateRangePickerReact displayContext={DisplayContext.ExperimentTimePicker} dateRange={experimentDateRange.getAttributes()} isTimePickerEnabled onApply={ev => {
                updateDraft({
                  start_date: ev.detail.dateRange.from ? moment(ev.detail.dateRange.from.toString()).format(DATE_FORMAT) : undefined,
                  end_date: ev.detail.dateRange.to ? moment(ev.detail.dateRange.to.toString()).format(DATE_FORMAT) : undefined
                });
                setIsDateOpen(false);
              }} onCancel={() => {
                setIsDateOpen(false);
              }} data-sentry-element="DateRangePickerReact" data-sentry-source-file="index.tsx" />
              </div>
            </SelectReact>
          </div>

          <div className={styles.formActions}>
            <ButtonReact theme={ButtonTheme.Tertiary} onClick={() => discardDraft()} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
              {i18n.t("common.cancel")}
            </ButtonReact>
            <ButtonReact theme={ButtonTheme.Primary} disabled={!canSave} onClick={() => handleSaveExperiment()} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
              {i18n.t("common.save")}
            </ButtonReact>
          </div>
        </div>
      </div>
    </ModalReact>;
}